import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NetIncomeDashboardModel, Tab } from 'models';
import { endOfMonth } from 'date-fns';

import {
  Centered,
  Loader,
  Error,
  Tabs,
  DatePicker,
  ChartBarNetIncome,
} from 'components';
import { useStyles } from './style';

interface Props {
  activeTab: string;
  dashboard: NetIncomeDashboardModel;
  startDate: Date | null;
  endDate: Date | null;
  periodEndingDate: Date | null;
  loading: boolean;
  refresh: boolean;
  error: boolean;
  handleChangeTab: (id: string) => void;
  handleChangeDate: (date: Date | null, name?: string) => void;
}

const NetIncomeAddonDetails: React.FC<Props> = ({
  activeTab,
  dashboard,
  startDate,
  endDate,
  periodEndingDate,
  loading,
  refresh,
  error,
  handleChangeTab,
  handleChangeDate,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const tabsClasses = useMemo(
    () => ({
      ul: classes.tabsRoot,
      tab: classes.tab,
      tabRoot: classes.tabRoot,
    }),
    [classes]
  );

  const datePickerClasses = useMemo(
    () => ({
      output: {
        root: classes.datePicker,
      },
    }),
    [classes]
  );

  const tabs: Tab[] = useMemo(() => {
    return [
      {
        id: '0',
        label: t('#portal.investor.dashboard.netincomes.period'),
        warning: false,
      },
      {
        id: '1',
        label: t('#portal.investor.dashboard.netincomes.currentyear'),
        warning: false,
      },
    ];
  }, [t]);

  const renderBody = () => {
    return (
      <div>
        <p className={classes.title}>
          {t('#portal.investor.dashboard.netincomes')}
        </p>
        <Tabs
          activeTab={activeTab}
          tabs={tabs}
          onSelect={handleChangeTab}
          cls={tabsClasses}
        />
        <div className={classes.datePickerWrapper}>
          <DatePicker
            name="startDate"
            date={startDate}
            handleChange={handleChangeDate}
            cls={datePickerClasses}
            maxDate={endDate}
          />
          <span className={classes.divider}>&mdash;</span>
          <DatePicker
            name="endDate"
            date={endDate}
            handleChange={handleChangeDate}
            cls={datePickerClasses}
            minDate={startDate}
            maxDate={periodEndingDate && endOfMonth(periodEndingDate)}
          />
        </div>
        <div className={classes.mt20}>
          {refresh ? (
            <Centered>
              <Loader size={30} themeColor="darkBlue" />
            </Centered>
          ) : (
            <ChartBarNetIncome dashboardData={dashboard} />
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {error ? (
        <Centered>
          <Error />
        </Centered>
      ) : null}
      {loading ? (
        <Centered>
          <Loader size={30} themeColor="darkBlue" />
        </Centered>
      ) : null}
      {loading || error ? null : renderBody()}
    </>
  );
};

export default memo(NetIncomeAddonDetails);
